import { cva } from 'class-variance-authority'

export const comboboxVariants = {
  anchor: {
    base: cva([
      'w-full',
      'flex',
      'items-center',
      'rounded-lg',
      'py-2.5xs',
      'gap-xs',
      'text-small',
      'outline',
      'outline-offset-2',
      'focus:outline-blurple-200',
    ], {
      variants: {
        intent: {
          filled: [
            'bg-surface-default',
            'px-0',
          ],
          outlined: [
            'px-s',
            'border',
            'border-border-default',
            'hover:border-border-primary',
            'hover:border-2',
            'hover:border-border-primary',
            'hover:px-[calc(theme(spacing.s)-1px)]',
            'hover:py-[calc(10px-1px)]',
          ],
        },
        selected: {
          true: [],
          false: [],
        },
        open: {
          true: ['border-border-primary'],
          false: [],
        },
        context: {
          workspace: [],
          download: [],
        },
      },
      compoundVariants: [
        {
          intent: 'outlined',
          selected: true,
          class: ['bg-surface-invert'],
        },
        {
          intent: 'filled',
          selected: [false, true],
          class: ['bg-transparent'],
        },
        {
          intent: 'outlined',
          selected: [true, false],
          context: 'workspace',
          class: ['bg-transparent'],
        },
      ],
    }),

    trigger: cva([], {
      variants: {
        selected: {
          true: [],
          false: [],
        },
        intent: {
          filled: [],
          outlined: [],
        },
        context: {
          workspace: [],
        },
      },
      compoundVariants: [
        {
          intent: 'outlined',
          selected: true,
          class: ['text-inverse'],
        },
        {
          intent: ['outlined'],
          selected: false,
          class: ['text-primary'],
        },
        {
          intent: 'filled',
          selected: [true, false],
          class: ['text-primary'],
        },
        {
          intent: 'outlined',
          selected: [true, false],
          context: 'workspace',
          class: ['text-primary'],
        },
      ],
    }),

    input: cva([
      'truncate',
      'outline-none',
      'bg-transparent',
      'h-auto',
      'w-full',
      'text-small',
    ], {
      variants: {
        selected: {
          true: ['text-inverse'],
          false: ['text-primary'],
        },
        intent: {
          filled: [],
          outlined: [],
        },
        searchable: {
          true: [],
          false: [],
        },
        context: {
          workspace: [],
        },
      },
      compoundVariants: [
        {
          intent: 'outlined',
          selected: true,
          class: ['text-inverse'],
        },
        {
          intent: 'outlined',
          selected: false,
          class: ['placeholder:text-primary', 'text-primary'],
        },
        {
          intent: 'filled',
          selected: [true, false],
          class: ['text-primary'],
        },
        {
          searchable: true,
          selected: true,
          class: ['placeholder:text-inverse'],
        },
        {
          intent: 'outlined',
          selected: [true, false],
          context: 'workspace',
          class: ['text-primary', 'placeholder:text-primary'],
        },
      ],
    }),
  },

  content: {
    base: cva([
      'absolute',
      'bg-surface-elevated',
      'rounded-lg',
      'border',
      'border-border-primary',
      'shadow-sm',
      'z-10',
      'mt-3xs',
      'absolute',
      'overflow-hidden',
      'transition-all',
      'duration-500',
      'ease-in-out',
      'shrink-0',
    ], {
      variants: {
        align: {
          start: 'start-0',
          end: 'end-0',
        },
        context: {
          alphabet: 'w-[190px]',
          default: ['w-max', 'max-h-96', 'overflow-y-auto'],
          workspace: 'w-fit',
          download: 'w-[214px]',
          legal: [],
        },
      },
      compoundVariants: [
        {
          context: ['default', 'legal'],
          class: [
            'max-h-96',
            'overflow-y-auto',
          ],
        },
      ],
    }),

    empty: cva([
      'py-s',
      'px-m',
      'text-small',
      'text-blurple-500',
    ]),

    item: cva([
      'flex',
      'items-center',
      'gap-s',
      'cursor-pointer',
      'text-primary',
      'active:bg-blurple-200',
    ], {
      variants: {
        context: {
          default: [],
          author: [],
          legal: [],
          alphabet: [
            'w-10',
            'h-10',
            'bg-carbon-50',
            'rounded-lg',
            'p-0',
            'justify-center',
            'data-[highlighted]:bg-surface-hover',
            'data-[state="checked"]:bg-blurple-500',
            'data-[state="checked"]:text-inverse',
          ],
          search: [
            'px-s',
            'py-xs',
            'text-small',
            'w-[160px]',
            'justify-between',
          ],
          workspace: [
            'px-s',
            'py-xs',
            'text-small',
            'justify-between',
            'relative',
          ],
          download: [
            'px-s',
            'py-xs',
            'text-small',
            'w-[342px]',
            'justify-between',
            'relative',
          ],
        },
        intent: {
          filled: [
            'data-[state="checked"]:bg-blurple-500',
            'data-[state="checked"]:text-inverse',
          ],
          outlined: [],
        },
      },
      compoundVariants: [
        {
          context: ['default', 'author', 'legal'],
          class: [
            'px-m',
            'py-s',
            'text-small',
            'justify-between',
            'data-[highlighted]:bg-surface-hover',
            'data-[state="checked"]:font-medium',
            'data-[state="checked"]:tracking-[-0.115px]',
          ],
        },
      ],
    }),

    indicator: cva([
      'shrink-0',
      'flex',
      'items-center',
      'justify-center',
    ], {
      variants: {
        context: {
          workspace: [
            'h-6',
            'w-6',
            'relative',
            'before:absolute',
            'before:w-6',
            'before:h-6',
            'before:rounded-full',
            'before:bg-surface-hover',
            'after:absolute',
            'after:w-3.5',
            'after:h-3.5',
            'after:rounded-[50%]',
            'after:bg-brand',
          ],
          default: [],
          author: [],
          download: [],
          alphabet: [],
          legal: [],
        },
      },
      compoundVariants: [
        {
          context: ['default', 'author', 'download', 'alphabet', 'legal'],
          class: ['size-full', 'rounded', 'bg-blurple-300'],
        },
      ],
    }),
  },
}
